// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {ChartTooltipEl} from 'common/components/chart-tooltip/panel';
import {SvgIconEl} from 'common/components/svg-icon';
import {TooltipEl} from 'common/components/tooltip';
import {ScrollControlEl} from 'common/widgets/scroll-control';
import {ChartLegendEl} from 'common/components/chart-legend/panel';
import {SelectTheme} from 'common/components/select/constants';
import {SelectEl} from 'common/components/select';
import {Placement} from 'common/components/tooltip/types';
import {formatMetricNumber} from 'common/util';
import {ChartLegendTheme} from 'common/components/chart-legend/constants';
import {getAccessibleForegroundColor} from 'common/util/colors';
import {Direction} from 'common/components/resizable-panel/types';

import type {SankeyChart} from './index';
import {SankeyNode, SankeyNodeAttributes} from './models/sankey-node';
import {SankeyStepType} from './models/types';
import {LinkTooltipPanel} from './link-tooltip-panel';

const NodeLabel = (props: {
  node: SankeyNode;
  nodeAttributes: SankeyNodeAttributes;
  anchorStepWeight: number;
  disableLift: boolean;
  disableAnchorEventLabels: boolean;
  qbAlignment: Direction;
  renameEvents: (name: string) => string;
  getAnchorLabelOverride: (anchorIndex: number, fallbackLabel: string) => string;
  getLabelIcon: (node: SankeyNode) => string;
  getLiftIcon: (isNegative: boolean) => string;
  getLiftTooltipText: (lift: string, isNegative: boolean) => string;
  getWeightTooltipText: (
    weight: string,
    stepIndex: number,
    nodeSection: number,
    stepType: SankeyStepType,
    anchorStepIndex: number,
    nodeLabel: string,
    isAnchorNode: boolean,
  ) => string;
}) => {
  const node = props.node;
  const nodeAttributes = props.nodeAttributes;

  const isAnchorNode = node.isAnchorNode();
  const nodeLabel = isAnchorNode
    ? props.getAnchorLabelOverride(node.getAnchorIndex(), node.eventString)
    : props.renameEvents(node.eventString);
  const labelIcon = props.getLabelIcon(node);
  const weight = formatMetricNumber((node.totalCount / props.anchorStepWeight) * 100);
  const totalCount = formatMetricNumber(node.totalCount);
  const negativeLift = node.conversionRateChange < 0;
  const lift = formatMetricNumber(Math.abs(node.conversionRateChange));
  const tooltipPlacement = props.qbAlignment === Direction.Right ? Placement.LeftCenter : Placement.RightCenter;

  return (
    <div sel='.sankey-node-label-wrapper' key={`label-${node.id}`}>
      <div
        sel='.sankey-node-label'
        props={{node, nodeAttributes}}
        class={{
          'disable-click': nodeAttributes.disableClick,
          'is-in-top-path': nodeAttributes.isInTopPath,
        }}
      >
        <div sel='.sankey-node-label-content'>
          <div sel='.sankey-node-name-wrapper'>
            <>
              {!props.disableAnchorEventLabels && labelIcon && (
                <div sel='.sankey-node-icons'>
                  <SvgIconEl attrs={{icon: labelIcon}} />
                </div>
              )}
              <div sel='.sankey-label-content'>
                <div sel='.sankey-node-name'>
                  {nodeLabel}
                  <TooltipEl attrs={{placement: tooltipPlacement, 'max-width': `300px`}}>{`${nodeLabel}`}</TooltipEl>
                </div>
                <div sel='.sankey-label-percentages'>
                  <div sel='.sankey-node-weight'>
                    <>
                      <div sel='.sankey-node-weight-percentage'>{`${weight}%`}</div>
                      <div sel='.sankey-node-weight-count'>{totalCount}</div>
                      <TooltipEl attrs={{placement: tooltipPlacement, 'max-width': `300px`}}>
                        {props.getWeightTooltipText(
                          weight,
                          node.step,
                          node.section,
                          nodeAttributes.stepType,
                          nodeAttributes.anchorStepIndex,
                          nodeLabel,
                          isAnchorNode,
                        )}
                      </TooltipEl>
                    </>
                  </div>
                  {!props.disableLift && node.hasLift() && (
                    <div sel='.sankey-node-lift' class={{'negative-lift': negativeLift}}>
                      <>
                        <SvgIconEl attrs={{icon: props.getLiftIcon(negativeLift)}} />
                        {`${lift}%`}
                        <TooltipEl attrs={{placement: tooltipPlacement, 'max-width': `300px`}}>
                          {props.getLiftTooltipText(lift, negativeLift)}
                        </TooltipEl>
                      </>
                    </div>
                  )}
                </div>
              </div>
            </>
          </div>
          {node.segments.map((segment) => (
            <div sel='.sankey-segment-value.sankey-node-name'>
              {`${segment.value}`}
              <TooltipEl attrs={{placement: tooltipPlacement, 'max-width': `300px`}}>
                {`${segment.label}: ${segment.value}`}
              </TooltipEl>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export function template(this: SankeyChart) {
  const {linkTooltip, links, nodeTooltip, selectedBreakdown, selectedNode, topPath} = this.state;
  const {t} = this.getContext(`i18n`);
  const qbAlignment = this.getContext(`reportLayoutManager`).getQbAlignment();

  const steps = this.data.steps;
  const breakdowns = this.data.breakdowns;
  const addStepsText = t(`flows.add-steps`);
  const anchorStep = this.helpers.getFlowAnchorStep();

  return (
    <div
      sel='.sankey'
      class={{
        'is-loaded': steps && !this.helpers.isEmptyFlow() ? links !== null : true,
        'is-reverse': this.attr(`reverse`),
        'is-top-path-mode': topPath.size > 0,
      }}
      hook={{
        insert: this.helpers.onSankeyInsert,
      }}
    >
      {steps && this.helpers.isEmptyFlow() && <slot attrs={{name: `empty-state`}} />}
      {steps && !this.helpers.isEmptyFlow() && (
        <>
          {linkTooltip ? (
            <LinkTooltipPanel {...linkTooltip} renameEvents={this.helpers.renameEvents} />
          ) : nodeTooltip ? (
            <ChartTooltipEl
              sections={nodeTooltip.sections}
              positioning={{clientX: nodeTooltip.clientX, clientY: nodeTooltip.clientY}}
              renderInPortal
            />
          ) : null}
          <ScrollControlEl
            attrs={{
              'scroll-by': this.helpers.getScrollByAmount(),
            }}
            props={{
              mousemoveEl: this.helpers.getMousemoveElement(),
              scrollEl: this.helpers.getScrollElement(),
            }}
          />
          <div
            sel='.sankey-table'
            hook={{
              insert: this.helpers.onTableInserted,
              postpatch: this.helpers.onTablePatched,
            }}
          >
            {breakdowns.length > 1 && (
              <div sel='.sankey-legend'>
                <ChartLegendEl
                  theme={ChartLegendTheme.CardTable}
                  sections={this.helpers.getBreakdownSections()}
                  wrapLabels
                />
              </div>
            )}
            {/*wrapper for scrolling inner table contents*/}
            <div sel='.sankey-table-scroller' key='sankey' hook={{insert: this.helpers.onScrollerInsert}}>
              <div sel='.sankey-table-content' hook={{insert: this.helpers.onTableContentInserted}}>
                <div sel='.sankey-table-headers'>
                  {steps.map((step, stepIndex) => {
                    const isSingleStep = steps.length <= 1;
                    const isLast = stepIndex === steps.length - 1;
                    const isFirst = stepIndex === 0;
                    const removeStepsByColumn = (step.isForward || step.isReverse) && !this.attr(`disable-add-step`);
                    const inRemoveRange = this.helpers.isInRemoveRange(step);

                    return (
                      <>
                        {step.canAddReverseStep && !this.attr(`disable-add-step`) && (
                          <div
                            sel='.sankey-table-column.break-header.reverse'
                            class={{'is-first': isFirst, 'is-single-step': isSingleStep}}
                          >
                            <div sel='.button-option-container'>
                              <SelectEl
                                attrs={{
                                  icon: {leftIcon: `plus`},
                                  'icon-only': !isSingleStep,
                                  'selected-option': addStepsText,
                                  'menu-width': null,
                                  'never-show-caret': true,
                                  'disable-search': true,
                                  theme: SelectTheme.Tertiary,
                                }}
                                props={{
                                  sections: this.helpers.getAddStepsSections(),
                                }}
                                on={{
                                  change: (ev) =>
                                    this.helpers.updateSteps({
                                      operation: `add`,
                                      type: `reverse`,
                                      anchor: step.anchor,
                                      absolute: this.helpers.getAbsoluteSteps({
                                        step,
                                        operation: `add`,
                                        value: ev.detail.item.value,
                                      }),
                                    }),
                                }}
                              />
                              {!isSingleStep && (
                                <TooltipEl attrs={{placement: isFirst ? Placement.Right : Placement.Bottom, offset: 5}}>
                                  <span>{addStepsText}</span>
                                </TooltipEl>
                              )}
                            </div>
                          </div>
                        )}
                        <div
                          sel='.sankey-table-column'
                          class={{
                            'in-remove-range': inRemoveRange,
                            'highlight-for-removal': removeStepsByColumn,
                            'has-context_menu': false,
                            'is-step-column': true,
                            'is-last': isLast,
                          }}
                          on={{
                            click: () =>
                              removeStepsByColumn
                                ? this.helpers.updateSteps({
                                    operation: `remove`,
                                    type: step.isForward ? `forward` : `reverse`,
                                    anchor: step.anchor,
                                    absolute: this.helpers.getAbsoluteSteps({
                                      step,
                                      operation: `remove`,
                                      value: 1,
                                    }),
                                  })
                                : {},
                            mouseenter: () => (removeStepsByColumn ? this.helpers.onRemoveStepHover(step) : {}),
                            mouseleave: () => (removeStepsByColumn ? this.helpers.onRemoveStepHover(step) : {}),
                          }}
                        >
                          <div sel='.sankey-header-content'>
                            {stepIndex !== 0 ? <div sel='.sankey-header-arrow-white' /> : null}
                            <div sel='.sankey-header-column-label-wrapper' class={{'is-first': isFirst}}>
                              {this.attr(`disable-anchor-event-labels`) ? (
                                <div sel='.sankey-header-column-name'>Step {stepIndex + 1}</div>
                              ) : (
                                <>
                                  <div sel='.sankey-header-column-letter-label'>{step.letterLabel}</div>
                                  <div sel='.sankey-header-column-name'>
                                    {(() => {
                                      const originalLabel = step.getStepLabel();
                                      const stepLabel =
                                        originalLabel && originalLabel.length > 20
                                          ? this.helpers.truncateLabel(originalLabel, 20)
                                          : originalLabel;

                                      return (
                                        <>
                                          {stepLabel}
                                          {originalLabel && originalLabel.length > 20 ? (
                                            <TooltipEl
                                              attrs={{
                                                placement: Placement.Bottom,
                                                offset: -5,
                                                'max-width': `300px`,
                                              }}
                                            >
                                              <span>{originalLabel}</span>
                                            </TooltipEl>
                                          ) : null}
                                        </>
                                      );
                                    })()}
                                  </div>
                                </>
                              )}
                            </div>
                            {removeStepsByColumn ? (
                              <div sel='.sankey-header-column-label-wrapper.remove-events'>
                                <div sel='.sankey-header-column-letter-label'>
                                  <SvgIconEl attrs={{icon: `dash`}} />
                                </div>
                                <div sel='.sankey-header-column-name'>
                                  {step.canAddForwardStep || step.canAddReverseStep
                                    ? t(`flows.remove-step`)
                                    : t(`flows.remove-steps`)}
                                </div>
                              </div>
                            ) : null}
                            {stepIndex !== steps.length - 1 ? <div sel='.sankey-header-arrow-gray' /> : null}
                          </div>
                        </div>
                        {step.canAddForwardStep && !this.attr(`disable-add-step`) && (
                          <div
                            sel='.sankey-table-column.break-header.forward'
                            class={{'is-single-step': isSingleStep, 'is-last': isLast}}
                          >
                            <div sel='.button-option-container'>
                              <SelectEl
                                attrs={{
                                  icon: {leftIcon: `plus`},
                                  'icon-only': !isSingleStep,
                                  'selected-option': addStepsText,
                                  'menu-width': null,
                                  'disable-search': true,
                                  'never-show-caret': true,
                                  theme: SelectTheme.Tertiary,
                                }}
                                props={{
                                  sections: this.helpers.getAddStepsSections(),
                                  boundaryEl: this.helpers.getScrollElement(),
                                }}
                                on={{
                                  change: (ev) =>
                                    this.helpers.updateSteps({
                                      operation: `add`,
                                      type: `forward`,
                                      anchor: step.anchor,
                                      absolute: this.helpers.getAbsoluteSteps({
                                        step,
                                        operation: `add`,
                                        value: ev.detail.item.value,
                                      }),
                                    }),
                                }}
                              />
                              {!isSingleStep ? (
                                <TooltipEl attrs={{placement: isLast ? Placement.Left : Placement.Bottom, offset: 5}}>
                                  <span>{addStepsText}</span>
                                </TooltipEl>
                              ) : null}
                            </div>
                          </div>
                        )}
                        {step.canAddForwardStep && stepIndex < steps.length - 1 && (
                          <div sel='.sankey-table-column.break-header.break'>
                            <SvgIconEl attrs={{icon: `wave`}} />
                            <div sel='.break' />
                          </div>
                        )}
                      </>
                    );
                  })}
                </div>
                <div sel='.sankey-table-body'>
                  {/*link shapes for all nodes*/}
                  <svg sel='.sankey-links'>
                    {(links || []).map((link) => (
                      <path
                        attrs={link.attrs}
                        class={link.class}
                        props={link.props}
                        on={link.on}
                        style={{'--breakdown-bg-color': link.color}}
                      />
                    ))}
                  </svg>
                  {steps.map((step, stepIndex) => {
                    const isSingleStep = steps.length <= 1;
                    const isLast = stepIndex === steps.length - 1;
                    const isFirst = stepIndex === 0;

                    return (
                      <>
                        {step.canAddReverseStep && !this.attr(`disable-add-step`) && (
                          <div
                            sel='.sankey-table-column.break-body.reverse'
                            class={{'is-first': isFirst, 'is-single-step': isSingleStep}}
                          />
                        )}
                        <div sel='.sankey-table-column' class={{'is-step-column': true, 'is-last': isLast}}>
                          {step.nodes.length > 0 ? (
                            step.nodes.map((node) => {
                              const nodeAttributes = this.helpers.getNodeAttributes(node, step);
                              const disableClick = !!this.attr(`disable-click-node`);

                              return (
                                <div sel='.sankey-node-wrapper'>
                                  <NodeLabel
                                    node={node}
                                    nodeAttributes={nodeAttributes}
                                    anchorStepWeight={anchorStep.weight}
                                    disableLift={this.attr(`disable-lift`)}
                                    disableAnchorEventLabels={this.attr(`disable-anchor-event-labels`)}
                                    qbAlignment={qbAlignment}
                                    renameEvents={this.helpers.renameEvents}
                                    getAnchorLabelOverride={this.helpers.getAnchorLabelOverride}
                                    getLabelIcon={this.helpers.getLabelIcon}
                                    getLiftIcon={this.helpers.getLiftIcon}
                                    getLiftTooltipText={this.helpers.getLiftTooltipText}
                                    getWeightTooltipText={this.helpers.getWeightTooltipText}
                                  />
                                  <div
                                    sel='.sankey-node'
                                    class={{
                                      'is-in-top-path': nodeAttributes.isInTopPath,
                                      [`is-${nodeAttributes.nodeType}`]: true,
                                      'disable-click': disableClick,
                                    }}
                                    dataset={{id: node.id}}
                                    props={{node}}
                                  >
                                    {node.breakdowns.map((breakdown) => {
                                      const breakdownAttributes = this.helpers.getBreakdownAttributes(breakdown);

                                      return (
                                        <div
                                          sel='.sankey-node-breakdown'
                                          class={{
                                            'is-active': selectedNode === node.id && selectedBreakdown === breakdown.id,
                                            'in-remove-range': nodeAttributes.isInRemoveRange,
                                            'is-broken-down': breakdowns.length > 1,
                                            [`is-${nodeAttributes.nodeType}`]: true,
                                          }}
                                          style={{
                                            height: `${breakdownAttributes.height}px`,
                                            '--breakdown-bg-color': breakdownAttributes.colors.primary,
                                            '--breakdown-text-color': getAccessibleForegroundColor(
                                              breakdownAttributes.colors.primary,
                                            ),
                                          }}
                                          on={{
                                            click: (ev) => this.helpers.onBreakdownNodeClicked(ev, node, breakdown),
                                            mousemove: (ev) =>
                                              this.helpers.onBreakdownNodeMouseMove(ev, node, breakdown),
                                            mouseleave: this.helpers.onBreakdownNodeMouseLeave,
                                          }}
                                        >
                                          {breakdowns.length > 1 && (
                                            <>
                                              <div sel='.sankey-node-breakdown-nudger' />
                                              <div sel='.sankey-node-breakdown-percentage'>
                                                {`${formatMetricNumber(
                                                  (breakdown.totalCount / node.totalCount) * 100,
                                                )}%`}
                                              </div>
                                            </>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div sel='.sankey-empty-column'>{`No Data`}</div>
                          )}
                        </div>
                        {step.canAddForwardStep && !this.attr(`disable-add-step`) && (
                          <div
                            sel='.sankey-table-column.break-body.forward'
                            class={{'is-single-step': isSingleStep, 'is-last': isLast}}
                          />
                        )}
                        {step.canAddForwardStep && stepIndex < steps.length - 1 && (
                          <div sel='.sankey-table-column.break-body.break'>
                            <div sel='.break' />
                          </div>
                        )}
                      </>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
